import { derive, opponentSpecials, opponentStats, SPECIAL_INFO, type Derived } from "./rules.ts";
import type { Archetype, Hero, Opponent, SpecialId } from "./types.ts";

export const ARENA_CELLS = 12;
const START = [2, 9] as const;

export type AttackKind = "quick" | "normal" | "power";
export type Side = 0 | 1;

export const ATTACKS: Record<AttackKind, { name: string; acc: number; mul: number; en: number }> = {
  quick: { name: "Quick Peck", acc: 18, mul: 0.6, en: 4 },
  normal: { name: "Strike", acc: 0, mul: 1, en: 8 },
  power: { name: "Mighty Wallop", acc: -18, mul: 1.6, en: 16 },
};

export type Action =
  | { type: "advance" }
  | { type: "retreat" }
  | { type: "attack"; kind: AttackKind }
  | { type: "taunt" }
  | { type: "rest" }
  | { type: "special"; id: SpecialId };

export type Outcome = "miss" | "block" | "hit";

export type BattleEvent =
  | { t: "move"; who: Side; from: number; to: number }
  | { t: "swing"; who: Side; kind: AttackKind | "frenzy"; outcome: Outcome; dmg: number; crit: boolean }
  | { t: "egg"; who: Side; outcome: Outcome; dmg: number }
  | { t: "taunt"; who: Side; ok: boolean }
  | { t: "roar"; who: Side; ok: boolean }
  | { t: "rest"; who: Side; hp: number; en: number }
  | { t: "heal"; who: Side; hp: number }
  | { t: "stunned"; who: Side }
  | { t: "ko"; who: Side };

export interface Fighter {
  name: string;
  level: number;
  d: Derived;
  hp: number;
  en: number;
  pos: number;
  hype: number;
  specials: SpecialId[];
  archetype: Archetype;
  eggs: number;
  windUsed: boolean;
  roarCd: number;
  stunned: boolean;
  rattled: boolean;
}

function fighter(name: string, level: number, d: Derived, specials: SpecialId[], archetype: Archetype, side: Side): Fighter {
  return {
    name,
    level,
    d,
    hp: d.maxHp,
    en: d.maxEn,
    pos: START[side],
    hype: 20,
    specials,
    archetype,
    eggs: 2,
    windUsed: false,
    roarCd: 0,
    stunned: false,
    rattled: false,
  };
}

export function heroFighter(h: Hero): Fighter {
  return fighter(h.name, h.level, derive(h.base, h.gear), h.specials, "balanced", 0);
}

export function opponentFighter(o: Opponent): Fighter {
  return fighter(o.name, o.level, derive(opponentStats(o), o.gear), o.specials ?? opponentSpecials(o), o.archetype, 1);
}

/** Maps a stat matchup to [-1, 1]: 0 when equal, ~0.33 when double. */
function edge(a: number, b: number): number {
  return (a - b) / (a + b);
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export class Battle {
  readonly f: [Fighter, Fighter];
  winner: Side | null = null;

  constructor(
    hero: Fighter,
    foe: Fighter,
    private readonly rand: () => number = Math.random,
  ) {
    this.f = [hero, foe];
  }

  dist(): number {
    return Math.abs(this.f[1].pos - this.f[0].pos);
  }

  inReach(who: Side): boolean {
    return this.dist() <= this.f[who].d.weapon.reach;
  }

  hitChance(who: Side, kind: AttackKind | "frenzy"): number {
    const a = this.f[who];
    const d = this.f[1 - who] as Fighter;
    const acc = kind === "frenzy" ? 5 : ATTACKS[kind].acc;
    const dodge = d.d.stats.agi * 0.55 + d.d.stats.def * 0.45;
    let c = 72 + acc + a.d.weapon.acc + 45 * edge(a.d.stats.atk, dodge);
    if (a.rattled) c -= 15;
    if (d.stunned) c += 15;
    return Math.round(clamp(c, 8, 97));
  }

  eggChance(who: Side): number {
    const a = this.f[who];
    const d = this.f[1 - who] as Fighter;
    return Math.round(clamp(80 + 40 * edge(a.d.stats.atk, d.d.stats.agi) - this.dist() * 2, 25, 95));
  }

  chaChance(who: Side, base: number, lo: number, hi: number): number {
    const a = this.f[who];
    const d = this.f[1 - who] as Fighter;
    return Math.round(clamp(base + 60 * edge(a.d.stats.cha, d.d.stats.cha), lo, hi));
  }

  armorMul(target: Side, pierce = 1): number {
    return 60 / (60 + this.f[target].d.armor * 2.2 * pierce);
  }

  /** Expected [min, max] damage on an unblocked, non-critical hit. */
  dmgRange(who: Side, kind: AttackKind | "frenzy"): [number, number] {
    const a = this.f[who];
    const w = a.d.weapon;
    const mul = (kind === "frenzy" ? 0.55 : ATTACKS[kind].mul) * this.armorMul((1 - who) as Side) * (a.hype >= 80 ? 1.15 : 1);
    const bonus = a.d.stats.str * 0.9;
    return [Math.max(1, Math.round((w.min + bonus) * mul)), Math.max(1, Math.round((w.max + bonus) * mul))];
  }

  eggDmg(who: Side): number {
    const a = this.f[who];
    return Math.max(1, Math.round((6 + a.level * 1.6 + a.d.stats.str * 0.4) * this.armorMul((1 - who) as Side, 0.5)));
  }

  /** Why an action is unavailable, or null when it can be taken. */
  blocked(who: Side, a: Action): string | null {
    const me = this.f[who];
    const dist = this.dist();
    switch (a.type) {
      case "advance":
        return dist <= 1 ? "Already beak to beak" : null;
      case "retreat": {
        const dir = who === 0 ? -1 : 1;
        const next = me.pos + dir;
        return next < 0 || next >= ARENA_CELLS ? "Backed against the wall" : null;
      }
      case "attack":
        if (!this.inReach(who)) return "Too far away";
        return me.en < ATTACKS[a.kind].en ? "Not enough energy" : null;
      case "taunt":
        return me.en < 3 ? "Not enough energy" : null;
      case "rest":
        return me.en >= me.d.maxEn && me.hp >= me.d.maxHp ? "Already fresh" : null;
      case "special": {
        if (!me.specials.includes(a.id)) return "Not learned";
        if (me.en < SPECIAL_INFO[a.id].energy) return "Not enough energy";
        if (a.id === "egg") return me.eggs <= 0 ? "Out of eggs" : null;
        if (a.id === "roar") return me.roarCd > 0 ? `Recovering (${me.roarCd})` : null;
        if (a.id === "frenzy") return this.inReach(who) ? null : "Too far away";
        return me.windUsed ? "Already used" : null;
      }
    }
  }

  private strike(who: Side, kind: AttackKind | "frenzy"): BattleEvent {
    const a = this.f[who];
    const target = (1 - who) as Side;
    const d = this.f[target];
    if (this.rand() * 100 >= this.hitChance(who, kind)) {
      a.hype = clamp(a.hype - 3, 0, 100);
      return { t: "swing", who, kind, outcome: "miss", dmg: 0, crit: false };
    }
    const [lo, hi] = this.dmgRange(who, kind);
    let dmg = lo + this.rand() * (hi - lo);
    const crit = this.rand() * 100 < Math.min(30, 4 + a.d.stats.cha * 0.4);
    if (crit) dmg *= 1.5;
    const blockedHit = !d.stunned && d.d.block > 0 && this.rand() * 100 < d.d.block;
    if (blockedHit) dmg *= 0.35;
    const final = Math.max(1, Math.round(dmg));
    d.hp = Math.max(0, d.hp - final);
    a.hype = clamp(a.hype + (crit ? 12 : kind === "power" ? 9 : 5), 0, 100);
    return { t: "swing", who, kind, outcome: blockedHit ? "block" : "hit", dmg: final, crit };
  }

  /** Resolve one action for `who`, mutating state. */
  act(who: Side, a: Action): BattleEvent[] {
    const me = this.f[who];
    const foeSide = (1 - who) as Side;
    const foe = this.f[foeSide];
    const ev: BattleEvent[] = [];
    let rested = false;

    if (me.stunned) {
      me.stunned = false;
      ev.push({ t: "stunned", who });
    } else {
      const why = this.blocked(who, a);
      if (why) throw new Error(`illegal action ${a.type}: ${why}`);
      const dir = who === 0 ? 1 : -1;
      switch (a.type) {
        case "advance": {
          const extra = this.rand() < 0.2 + me.d.stats.agi * 0.015 ? 1 : 0;
          const steps = Math.min(1 + extra, this.dist() - 1);
          const from = me.pos;
          me.pos += dir * steps;
          ev.push({ t: "move", who, from, to: me.pos });
          break;
        }
        case "retreat": {
          const extra = this.rand() < 0.25 + me.d.stats.agi * 0.015 ? 1 : 0;
          const from = me.pos;
          me.pos = clamp(me.pos - dir * (1 + extra), 0, ARENA_CELLS - 1);
          ev.push({ t: "move", who, from, to: me.pos });
          break;
        }
        case "attack":
          me.en -= ATTACKS[a.kind].en;
          ev.push(this.strike(who, a.kind));
          me.rattled = false;
          break;
        case "taunt": {
          me.en -= 3;
          const ok = this.rand() * 100 < this.chaChance(who, 55, 15, 92);
          if (ok) {
            foe.en = Math.max(0, foe.en - Math.round(5 + me.d.stats.cha * 0.5));
            foe.rattled = true;
            me.hype = clamp(me.hype + 18, 0, 100);
          } else me.hype = clamp(me.hype - 8, 0, 100);
          ev.push({ t: "taunt", who, ok });
          break;
        }
        case "rest": {
          rested = true;
          const en = Math.min(me.d.maxEn - me.en, Math.round(me.d.maxEn * 0.35));
          const hp = Math.min(me.d.maxHp - me.hp, Math.round(me.d.maxHp * 0.05));
          me.en += en;
          me.hp += hp;
          me.hype = clamp(me.hype - 5, 0, 100);
          ev.push({ t: "rest", who, hp, en });
          break;
        }
        case "special":
          me.en -= SPECIAL_INFO[a.id].energy;
          if (a.id === "egg") {
            me.eggs--;
            const hit = this.rand() * 100 < this.eggChance(who);
            const dmg = hit ? this.eggDmg(who) : 0;
            foe.hp = Math.max(0, foe.hp - dmg);
            if (hit) me.hype = clamp(me.hype + 10, 0, 100);
            ev.push({ t: "egg", who, outcome: hit ? "hit" : "miss", dmg });
          } else if (a.id === "roar") {
            me.roarCd = 4;
            const ok = this.rand() * 100 < this.chaChance(who, 45, 15, 80);
            if (ok) foe.stunned = true;
            me.hype = clamp(me.hype + (ok ? 14 : 4), 0, 100);
            ev.push({ t: "roar", who, ok });
          } else if (a.id === "frenzy") {
            for (let i = 0; i < 3 && foe.hp > 0; i++) ev.push(this.strike(who, "frenzy"));
            me.rattled = false;
          } else {
            me.windUsed = true;
            const hp = Math.min(me.d.maxHp - me.hp, Math.round(me.d.maxHp * 0.35));
            me.hp += hp;
            ev.push({ t: "heal", who, hp });
          }
          break;
      }
    }

    if (!rested) me.en = Math.min(me.d.maxEn, me.en + 3);
    if (me.roarCd > 0) me.roarCd--;
    if (foe.hp <= 0) {
      this.winner = who;
      ev.push({ t: "ko", who: foeSide });
    }
    return ev;
  }

  ai(who: Side): Action {
    const me = this.f[who];
    const r = this.rand();
    const ok = (a: Action) => this.blocked(who, a) === null;
    const sp = (id: SpecialId): Action => ({ type: "special", id });
    const hpR = me.hp / me.d.maxHp;
    const enR = me.en / me.d.maxEn;

    if (hpR < 0.4 && ok(sp("wind"))) return sp("wind");

    if (this.inReach(who)) {
      if (r < 0.15 && ok(sp("roar"))) return sp("roar");
      if (r < 0.3 && ok(sp("frenzy"))) return sp("frenzy");
      if (me.archetype === "showman" && r > 0.88 && ok({ type: "taunt" })) return { type: "taunt" };
      const weights: Record<Archetype, [number, number, number]> = {
        brute: [1, 2, 3],
        duelist: [3, 2, 1],
        tank: [1, 3, 1.5],
        showman: [2, 2, 2],
        balanced: [1.5, 3, 2],
      };
      const kinds: AttackKind[] = ["quick", "normal", "power"];
      const opts = kinds.map((kind, i) => ({ kind, w: ok({ type: "attack", kind }) ? (weights[me.archetype][i] ?? 1) : 0 }));
      const total = opts.reduce((s, o) => s + o.w, 0);
      if (total === 0) return r < 0.4 && ok({ type: "retreat" }) ? { type: "retreat" } : { type: "rest" };
      let roll = this.rand() * total;
      for (const o of opts) {
        roll -= o.w;
        if (roll <= 0 && o.w > 0) return { type: "attack", kind: o.kind };
      }
      return { type: "attack", kind: "quick" };
    }

    if (r < 0.35 && ok(sp("egg"))) return sp("egg");
    if (enR < 0.35 && this.dist() > 2 && ok({ type: "rest" })) return { type: "rest" };
    if (r > (me.archetype === "showman" ? 0.75 : 0.93) && this.dist() > 2 && ok({ type: "taunt" })) return { type: "taunt" };
    return ok({ type: "advance" }) ? { type: "advance" } : { type: "rest" };
  }
}
